/**
 * Phase 8: Middle section - Stages (top-left), Remote Speakers (bottom-left), Media (right).
 * Main Stages/Remote | Media row: column below md, row from md up (inline Control + overlay).
 */

import { remoteSpeakerItemId, remoteSpeakersForSection } from '@/helpers/remote-speaker-players.helper';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Visibility,
  InfoOutlined,
  InsertDriveFileOutlined,
  ScreenShareOutlined,
  VolumeUp,
  VolumeOff,
  VolumeMute,
  RestartAlt,
} from '@mui/icons-material';
import { SlideControlButton } from '@/components/events/detail/tabs/common/media/slide-control-button';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { FileType } from '@/types/dtos/media/media.asset.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useUpdateStagedSelectionMutation } from '@/state/mutations/player/use-update-staged-selection';
import { useToggleLayerAudibleMutation } from '@/state/mutations/player/use-toggle-layer-audible';
import type { MicAudibleOverrideMode } from '@/state/mutations/player/use-toggle-layer-audible';
import { useMemo, useState } from 'react';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import {
  isInfoOverlayComposite,
  useGetCompositeComponentsQuery,
} from '@/state/queries/composite-components/use-get-composite-components';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { useTranslations } from '@/i18n/use-translations';
import { useControlPreviewMutation } from '@/state/mutations/player/use-control-preview';
import { useNotifications } from '@/hooks/use-notifications';
import { LiveKitPreviewModal } from '@/components/preview/livekit-preview-modal';
import { PreviewModal, type PreviewProcessingInstructions } from '@/components/preview/preview-modal';
import { PlayCueConfirmationModal } from '@/components/media/play-cue-confirmation-modal';
import { MediaProcessingInstructionsModal } from '@/components/media/media-processing-instructions.modal';
import { PLAY_TYPE } from '@/types/dtos/player/request/control-device.dto';
import { resolvePlayTypeForCompositeSlug } from '@/helpers/composite-slug.helper';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import { FileTypeIcon } from '@/components/media/file-type-icon';
import { MediaItemProcessingSpinner } from '@/components/events/detail/tabs/common/media/media-item-processing-spinner';
import { hasPreviewableMedia } from '@/helpers/media-processing-insight.helper';
import {
  resolveStartSlideIndexForTake,
  seedLiveSlideIndexFromPreviewLayers,
  seedLocalMediaPreviewFromDurableResume,
} from '@/helpers/resolve-start-slide-index-for-take.helper';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import {
  hasShareableProcessingInstructions,
  buildMediaProcessingInstructionsContext,
} from '@/helpers/media-processing-instructions.helper';
import {
  mapMediaItemStatusToneToIconColor,
  resolveMediaItemAggregateStatus,
} from '@/helpers/media-item-aggregate-status.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { hasSlideSupport, resolveSlideControlMediaType } from '@/constants/media-types';
import { resolveMediaFrameSlideNumbers } from '@/helpers/presentation-step-position.helper';
import { useGetSequenceIndexStatusQuery } from '@/state/queries/player/use-get-sequence-index-status';
import { PerformShowModeRow } from './perform-show-mode-row';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import {
  cuePhaseToRowState,
  getPerformItemRowStyles,
  getPerformSelectableRowHoverSx,
  isPerformItemOnAir,
  resolveCueItemState,
} from '@/helpers/perform-staging.helper';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';

export interface PerformMiddleSectionProps {
  flow: FlowDto;
  selectedSlot?: FlowSlotDto;
  entityRef?: string;
  eventSlotId?: string;
  livekitRoomId?: string;
  liveContextState?: LiveContextStateDto;
  /** React Query `dataUpdatedAt` for live-context GET — seeds cue WS overlay on each fresh snapshot. */
  liveContextQueryUpdatedAt?: number;
  /** When true, show Media / Mixed / Speaker controls at the top (e.g. narrow Control tab inline expansion). */
  showModeSelector?: boolean;
  /** When true, use side-by-side columns at all breakpoints (narrow Control tab). */
  forceTwoColumnLayout?: boolean;
}

/** Virtual media item for selection UI. itemId format: virtualMedia:pageSlug */
type VirtualMediaEntry = { itemId: string; title: string };

type PerformMediaListItem =
  | { itemId: string; title: string; type: 'virtual'; media?: undefined; fileType?: undefined }
  | { itemId: string; title: string; type: 'screenshare'; media?: undefined; fileType?: undefined }
  | { itemId: string; title: string; type: 'media'; media: MediaDto; fileType?: FileType };

/** Shared audible-override Menu id (one menu, many row triggers). */
const PERFORM_AUDIBLE_MENU_ID = 'perform-audible-override-menu';

/** Stage prefixes: one at a time for stage slot (roomCam only). */
const STAGE_PREFIXES = ['roomCam:'];

/** Media prefixes: one at a time for media slot (composite, screenshare, media) */
const MEDIA_PREFIXES = ['media:', 'composite:', 'screenshare:'];

/** Toggle stage or media selection independently. Stages and Media can both be selected. */
function toggleMainContentInSelection(selectedItemIds: string[], itemId: string): string[] {
  const isSelected = selectedItemIds.includes(itemId);
  if (isSelected) {
    return selectedItemIds.filter((id) => id !== itemId);
  }
  const isStage = STAGE_PREFIXES.some((p) => itemId.startsWith(p));
  const withoutSameGroup = selectedItemIds.filter(
    (id) => !(isStage ? STAGE_PREFIXES : MEDIA_PREFIXES).some((p) => id.startsWith(p)),
  );
  return [...withoutSameGroup, itemId];
}

/** Map composite:slug itemId to control preview request params */
function getCompositePreviewRequest(
  itemId: string,
  flowId: string,
  eventId: string | undefined,
  selectedSlot: FlowSlotDto | undefined,
) {
  const slug = itemId.replace('composite:', '');
  const contextEntityId = flowId;
  const contextEntityType = 'Flow' as const;
  const playType = resolvePlayTypeForCompositeSlug(slug);

  switch (slug) {
    case 'event-info':
    case 'event-closing':
      return eventId
        ? {
            entityType: 'Event' as const,
            entityId: eventId,
            contextEntityId,
            contextEntityType,
            playType,
          }
        : null;
    case 'flow-info':
    case 'flow-title':
    case 'flow-agenda':
    case 'flow-closing':
      return {
        entityType: 'Flow' as const,
        entityId: flowId,
        contextEntityId,
        contextEntityType,
        playType,
      };
    case 'session-info':
      if (selectedSlot?.contentType === 'Session' && selectedSlot.content) {
        const session = selectedSlot.content as { id?: string };
        const sessionId = session?.id ?? getObjectId(selectedSlot.content);
        return sessionId
          ? {
              entityType: 'Session' as const,
              entityId: sessionId,
              contextEntityId: flowId,
              contextEntityType: 'Flow' as const,
              playType: PLAY_TYPE.SESSION_INFO,
            }
          : null;
      }
      return null;
    case 'break-info':
      if (selectedSlot?.contentType === 'Break' && selectedSlot.id) {
        return {
          entityType: 'Break' as const,
          entityId: selectedSlot.id,
          contextEntityId: flowId,
          contextEntityType: 'Flow' as const,
          playType: PLAY_TYPE.BREAK_INFO,
        };
      }
      return null;
    default:
      return null;
  }
}

export function PerformMiddleSection({
  flow,
  selectedSlot,
  entityRef,
  eventSlotId,
  livekitRoomId,
  liveContextState,
  showModeSelector = false,
  forceTwoColumnLayout = false,
}: PerformMiddleSectionProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const { hasAccess: hasAutoCue } = useFeatureAccess('perform_auto_cue');
  const cueCommand = useCueCommandMutation();
  const updateStagedSelection = useUpdateStagedSelectionMutation();
  const toggleLayerAudible = useToggleLayerAudibleMutation();
  const controlPreviewMutation = useControlPreviewMutation();
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  const usesAutoCue = hasAutoCue && !!cueState;
  const cueItems = cueState?.items;
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotId) : undefined;
  const eventId = flow?.parent
    ? typeof flow.parent === 'object' && 'id' in flow.parent
      ? flow.parent.id
      : String(flow.parent)
    : undefined;
  const liveReady = flow?.liveContext?.isRunning ?? false;
  const { data: players = [] } = useGetPlayersQuery(
    bindingFlowId
      ? {
          entityId: bindingFlowId,
          entityType: 'Flow',
          ...(eventSlotId ? { eventSlotId } : {}),
        }
      : undefined,
    {
      enabled: !!bindingFlowId,
    },
  );

  const connectedPlayer = useMemo(() => {
    if (!Array.isArray(players)) return undefined;
    const mediaAgentPlayer = players.find(
      (p) =>
        p.isConnected && p.type === 'mediaAgent' && (p.currentSlideIndex !== undefined || p.totalSlides !== undefined),
    );
    return mediaAgentPlayer || players.find((p) => p.isConnected);
  }, [players]);

  const { data: sequenceIndexStatus } = useGetSequenceIndexStatusQuery(entityRef, {
    enabled: !!entityRef && !!bindingFlowId,
  });

  const [liveKitPreview, setLiveKitPreview] = useState<{
    livekitRoomId: string;
    title: string;
    publisherPlayerId?: string;
    showCamera?: boolean;
    showScreenshare?: boolean;
  } | null>(null);
  const [mediaPreview, setMediaPreview] = useState<{
    playType: string;
    playerSyncData?: Record<string, unknown>;
    title: string;
    itemId: string;
    loading?: boolean;
    processingInstructions?: PreviewProcessingInstructions;
  } | null>(null);
  const [instructionsPreview, setInstructionsPreview] = useState<PreviewProcessingInstructions | null>(null);
  const [playConfirm, setPlayConfirm] = useState<{
    itemId: string;
    title: string;
    /** Captured at Play overlay / row click so confirmation cannot lose the preview choice. */
    startSlideIndex?: number;
  } | null>(null);

  const mediaItems = useMemo(() => {
    if (selectedSlot?.contentType === 'Session' && selectedSlot.content) {
      const session = selectedSlot.content as { media?: typeof flow.media };
      return session?.media ?? flow.media ?? [];
    }
    if (selectedSlot?.contentType === 'Break' && selectedSlot.id) {
      return flow.media?.filter((m) => m.placement?.id === selectedSlot.id) ?? [];
    }
    return flow.media ?? [];
  }, [flow, selectedSlot]);

  const selectedItemIds = liveContextState?.stagedSelection?.selectedItemIds ?? [];
  const speakersOnStage =
    useLiveContextStateWithLivePlayState(entityRef, liveContextState)?.remoteSpeakersOnStage === true;
  /** Map DTO showMode to API format: 'mixedView' → 'both' */
  const rawShowMode = liveContextState?.stagedSelection?.showMode ?? 'mediaOnly';
  const showMode: 'mediaOnly' | 'both' | 'speakerOnly' = rawShowMode === 'mixedView' ? 'both' : rawShowMode;
  /** Effective program audible per itemId (segment defaults + overrides). */
  const itemAudible = useMemo(() => {
    const overrides = liveContextState?.playState?.itemAudibleOverrides ?? {};
    const byPlayer = liveContextState?.playState?.byPlayer;
    const result = { ...overrides };
    for (const entry of Object.values(byPlayer ?? {})) {
      for (const layer of entry.layers ?? []) {
        if (layer.itemId != null && !(layer.itemId in result)) {
          result[layer.itemId] = layer.audible !== false;
        }
      }
    }
    return result;
  }, [liveContextState?.playState?.itemAudibleOverrides, liveContextState?.playState?.byPlayer]);

  const itemAudibleControl = liveContextState?.playState?.micAudibleControl?.overrides ?? {};
  const [audibleMenu, setAudibleMenu] = useState<{ anchor: HTMLElement; itemId: string } | null>(null);
  const playedItemIds = useMemo(() => {
    const entries = liveContextState?.playState?.playedItemIds;
    if (entries?.length) {
      return new Set(entries.map((e) => e.itemId));
    }
    return new Set((liveContextState?.playState?.layers ?? []).map((l) => l.itemId).filter((id): id is string => !!id));
  }, [liveContextState?.playState?.playedItemIds, liveContextState?.playState?.layers]);

  const resolveCueRowState = (itemId: string, legacySelected: boolean, legacyPlayed: boolean) => {
    if (!usesAutoCue) {
      return { isSelected: legacySelected, isPlayed: legacyPlayed };
    }
    const { staged, played } = cuePhaseToRowState(resolveCueItemState(cueItems, itemId)?.phase);
    return { isSelected: staged, isPlayed: played };
  };

  const handleCueTrackSelect = (itemId: string) => {
    if (!entityRef || cueCommand.isPending) return;
    cueCommand.mutate({ action: 'selectTrack', entityRef, itemId });
  };

  const executeCuePlayMedia = (
    itemId: string,
    cuePlayMode: 'jump' | 'insert' = 'insert',
    capturedStartSlideIndex?: number,
  ) => {
    if (!entityRef || cueCommand.isPending) return;
    const cueEntry = resolveCueItemState(cueItems, itemId);
    const resolved = resolveStartSlideIndexForTake(itemId, {
      playlistContext: {
        scope: cueEntry?.scope,
        slotId: cueEntry?.slotId,
      },
    });
    // Prefer a fresh operator pick from the still-open preview; else the index captured on Play.
    const startSlideIndex =
      (useLiveSequenceIndexStore.getState().isRecentlyOptimistic(itemId) ? resolved : undefined) ??
      (capturedStartSlideIndex != null && capturedStartSlideIndex > 0 ? capturedStartSlideIndex : undefined) ??
      resolved;
    cueCommand.mutate({
      action: 'playMedia',
      entityRef,
      itemId,
      cuePlayMode,
      ...(cueEntry?.cueItemId ? { cueItemId: cueEntry.cueItemId } : {}),
      ...(cueEntry?.scope ? { scope: cueEntry.scope } : {}),
      ...(cueEntry?.slotId ? { slotId: cueEntry.slotId } : {}),
      ...(startSlideIndex != null ? { startSlideIndex } : {}),
    });
  };

  const handleCueMediaItem = (itemId: string, title: string) => {
    if (!entityRef || cueCommand.isPending) return;
    const phase = resolveCueItemState(cueItems, itemId)?.phase;
    if (phase === 'on_air') {
      cueCommand.mutate({ action: 'stopMedia', entityRef, itemId });
      return;
    }
    setPlayConfirm({
      itemId,
      title,
      startSlideIndex: resolveStartSlideIndexForTake(itemId),
    });
  };

  const handleMainContentToggle = (itemId: string, title?: string) => {
    if (!entityRef) return;
    if (usesAutoCue && !itemId.startsWith('screenshare:')) {
      if (itemId.startsWith('roomCam:') || itemId.startsWith('remoteSpeaker:')) {
        handleCueTrackSelect(itemId);
      } else {
        handleCueMediaItem(itemId, title ?? itemId);
      }
      return;
    }
    const next = toggleMainContentInSelection(selectedItemIds, itemId);
    updateStagedSelection.mutate({
      entityRef,
      stagedSelection: {
        selectedItemIds: next,
        showMode,
        ...(next.length === 0 ? { clearSelection: true } : {}),
      },
    });
  };

  const openAudibleMenu = (e: React.MouseEvent<HTMLElement>, itemId: string) => {
    e.stopPropagation();
    setAudibleMenu({ anchor: e.currentTarget, itemId });
  };

  const closeAudibleMenu = () => setAudibleMenu(null);

  const applyAudibleOverride = (
    itemId: string,
    options: { mode?: MicAudibleOverrideMode; audible?: boolean; clearOverride?: boolean },
  ) => {
    if (!entityRef) return;
    toggleLayerAudible.mutate({ entityRef, itemId, ...options });
    closeAudibleMenu();
  };

  const renderAudibleControl = (itemId: string) => {
    if (!entityRef) return null;
    const isAudible = itemAudible[itemId] !== false;
    const control = itemAudibleControl[itemId];
    const title =
      control?.mode === 'permanent'
        ? isAudible
          ? t('flows.detail.perform.audioAlwaysOn', undefined, 'Always on (permanent)')
          : t('flows.detail.perform.audioAlwaysOff', undefined, 'Always muted (permanent)')
        : control?.mode === 'segment'
          ? isAudible
            ? t('flows.detail.perform.audioUntilSegmentOn', undefined, 'On until next segment')
            : t('flows.detail.perform.audioUntilSegmentOff', undefined, 'Muted until next segment')
          : isAudible
            ? t('flows.detail.perform.muteAudio')
            : t('flows.detail.perform.unmuteAudio');

    const menuOpenForItem = audibleMenu?.itemId === itemId;
    const buttonId = `perform-audible-override-button-${itemId}`;

    return (
      <Tooltip title={title}>
        <IconButton
          id={buttonId}
          size="small"
          onClick={(e) => openAudibleMenu(e, itemId)}
          disabled={toggleLayerAudible.isPending}
          aria-controls={menuOpenForItem ? PERFORM_AUDIBLE_MENU_ID : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpenForItem ? 'true' : undefined}
          aria-label={title}
          sx={{ p: 0.25 }}
        >
          {isAudible ? (
            <VolumeUp fontSize="small" color={control?.mode === 'permanent' ? 'primary' : 'inherit'} />
          ) : (
            <VolumeOff fontSize="small" color={control?.mode === 'permanent' ? 'primary' : 'inherit'} />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  // Stage = roomCam publishers only (stagePlayer). Media agent tracks come from presentation media, not this list.
  const stagePlayers = useMemo(
    () => (liveReady && Array.isArray(players) ? players.filter((p): p is PlayerDto => p.type === 'stagePlayer') : []),
    [players, liveReady],
  );

  // Media laptops (screenPublisher) are demo screens: their screen share is main content.
  const screensharePlayers = useMemo(
    () =>
      liveReady && Array.isArray(players)
        ? players.filter((p): p is PlayerDto => p.type === 'screenPublisher' && p.isConnected)
        : [],
    [players, liveReady],
  );

  // Remote speakers: consoles of the current section. They come on stage together via the lower-controls toggle.
  const remoteSpeakerPlayers = useMemo(
    () => (liveReady ? remoteSpeakersForSection(players, cueState?.activeSlotId) : []),
    [players, liveReady, cueState?.activeSlotId],
  );

  // Composite components from API (filtered by allowedIn for context)
  const apiContext = useMemo((): 'event' | 'flow' | 'session' | 'break' | undefined => {
    if (selectedSlot?.contentType === 'Session') return 'session';
    if (selectedSlot?.contentType === 'Break') return 'break';
    return 'flow';
  }, [selectedSlot?.contentType]);

  const { data: composites = [] } = useGetCompositeComponentsQuery(apiContext);

  /** Virtual media = playlist composites only. Info overlays are placed by the slide template. */
  const compositeMediaItems = useMemo((): VirtualMediaEntry[] => {
    return composites
      .filter((c) => !isInfoOverlayComposite(c))
      .map((c) => ({
        itemId: `composite:${c.slug}`,
        title: c.name,
      }));
  }, [composites]);

  const canPreviewLiveKit = (player: PlayerDto) => livekitRoomId && (player.isConnected || player.isLivekitConnected);

  const handleStagePreview = (e: React.MouseEvent, player: PlayerDto) => {
    e.stopPropagation();
    if (!livekitRoomId || !canPreviewLiveKit(player)) return;
    setLiveKitPreview({
      livekitRoomId,
      title: `${t('flows.detail.perform.stages')} - ${player.name ?? player.id}`,
      publisherPlayerId: player.id,
      showCamera: true,
      showScreenshare: false,
    });
  };

  const handleRemoteSpeakerPreview = (e: React.MouseEvent, player: PlayerDto) => {
    e.stopPropagation();
    if (!livekitRoomId || !canPreviewLiveKit(player)) return;
    setLiveKitPreview({
      livekitRoomId,
      title: `${player.name ?? player.id}`,
      publisherPlayerId: player.id,
      showCamera: true,
      showScreenshare: false,
    });
  };

  const handleMediaPreview = async (e: React.MouseEvent, item: PerformMediaListItem) => {
    e.stopPropagation();
    const resolveInstructions = (media: MediaDto): PreviewProcessingInstructions | undefined => {
      const aggregate = resolveMediaItemAggregateStatus(media);
      if (!aggregate.insight || !hasShareableProcessingInstructions(aggregate.insight)) {
        return undefined;
      }
      return {
        insight: aggregate.insight,
        context: buildMediaProcessingInstructionsContext({
          media,
          eventTitle: event?.title,
          parentTitle: flow.title,
        }),
      };
    };

    if (item.type === 'screenshare') {
      if (!livekitRoomId) return;
      const publisherPlayerId = item.itemId.startsWith('screenshare:')
        ? item.itemId.slice('screenshare:'.length)
        : undefined;
      setLiveKitPreview({
        livekitRoomId,
        title: item.title,
        publisherPlayerId,
        showCamera: false,
        showScreenshare: true,
      });
      return;
    }
    if (item.type === 'virtual') {
      const request = bindingFlowId
        ? getCompositePreviewRequest(item.itemId, bindingFlowId, eventId, selectedSlot)
        : null;
      if (!request) return;
      setMediaPreview({
        playType: request.playType,
        title: item.title,
        itemId: item.itemId,
        loading: true,
      });
      try {
        const response = await controlPreviewMutation.mutateAsync(request);
        setMediaPreview({
          playType: request.playType,
          playerSyncData: response.playerSyncData,
          title: item.title,
          itemId: item.itemId,
          loading: false,
        });
        notification.notifySuccess(t('media.notification.previewSuccess'));
      } catch (err) {
        console.error('Preview error:', err);
        setMediaPreview(null);
        notification.notifyError(t('media.notification.previewError'));
      }
      return;
    }

    const media = item.type === 'media' ? item.media : undefined;
    if (!media) {
      return;
    }

    const processingInstructions = resolveInstructions(media);
    if (!hasPreviewableMedia(media.assets ?? [])) {
      if (processingInstructions) {
        setInstructionsPreview(processingInstructions);
      }
      return;
    }

    const mediaId = item.itemId.replace('media:', '');
    // Item preview uses local slide nav — seed from mediaSequenceIndex (live store) before open.
    seedLocalMediaPreviewFromDurableResume(item.itemId);
    setMediaPreview({
      playType: PLAY_TYPE.MEDIA,
      title: item.title,
      itemId: item.itemId,
      loading: true,
      processingInstructions,
    });
    try {
      const response = await controlPreviewMutation.mutateAsync({
        entityType: 'Media',
        entityId: mediaId,
        contextEntityId: bindingFlowId ?? '',
        contextEntityType: 'Flow',
        playType: PLAY_TYPE.MEDIA,
      });
      const playerSyncData = response.playerSyncData as Record<string, unknown> | undefined;
      seedLiveSlideIndexFromPreviewLayers(item.itemId, playerSyncData);
      seedLocalMediaPreviewFromDurableResume(item.itemId);
      setMediaPreview({
        playType: PLAY_TYPE.MEDIA,
        playerSyncData,
        title: item.title,
        itemId: item.itemId,
        loading: false,
        processingInstructions,
      });
      notification.notifySuccess(t('media.notification.previewSuccess'));
    } catch (err) {
      console.error('Preview error:', err);
      setMediaPreview(null);
      notification.notifyError(t('media.notification.previewError'));
    }
  };

  const allMediaItems = useMemo(
    () => [
      ...compositeMediaItems.map((v) => ({ ...v, type: 'virtual' as const, media: undefined })),
      ...screensharePlayers.map((player, index) => ({
        itemId: `screenshare:${player.id}`,
        title: `${t('common.track')} - ${player.name ?? `Player ${index + 1}`}`,
        type: 'screenshare' as const,
        media: undefined,
      })),
      ...mediaItems
        .filter((m) => !m.placement)
        .map((m) => {
          const sortedAssets = [...(m.assets ?? [])].sort((a, b) => b.version - a.version);
          const latestAsset = sortedAssets[0];
          return {
            itemId: `media:${m.id}`,
            title: m.title ?? 'Untitled',
            type: 'media' as const,
            media: m,
            fileType: latestAsset?.fileType,
          };
        }),
    ],
    [compositeMediaItems, screensharePlayers, mediaItems, t],
  );

  const itemRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    alignSelf: 'stretch',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
  };

  const itemTitleSx: SxProps<Theme> = {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  /** Inline: side-by-side from md; stacked below md — use xs flex-basis auto so columns do not stretch in column mode */
  const colFlex = forceTwoColumnLayout
    ? { flex: { xs: '0 0 auto', md: '1 1 0' }, width: '100%', minWidth: 0 }
    : { flex: { xs: '0 0 auto', md: '1 1 0' }, width: { xs: '100%' }, minWidth: { md: 0 } };

  /** Tighter vertical rhythm in narrow Control tab inline two-column layout */
  const sectionContentPy = forceTwoColumnLayout ? 1 : 2;
  const leftColumnStackGap = forceTwoColumnLayout ? 1 : 2;

  /** Inline Control tab: omit empty Stages/Remote blocks (no placeholder copy) */
  const inlineHideEmptyStagesRemote = forceTwoColumnLayout;
  const hasStages = stagePlayers.length > 0;
  const hasRemoteSpeakers = remoteSpeakerPlayers.length > 0;
  const showStagesPaper = !inlineHideEmptyStagesRemote || hasStages;
  const showRemoteSpeakersPaper = !inlineHideEmptyStagesRemote || hasRemoteSpeakers;
  const showLeftColumn = !inlineHideEmptyStagesRemote || hasStages || hasRemoteSpeakers;
  const showStagesRemoteDivider = !inlineHideEmptyStagesRemote || (hasStages && hasRemoteSpeakers);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: forceTwoColumnLayout ? '0 0 auto' : 1,
        minHeight: forceTwoColumnLayout ? 'auto' : 0,
        flexDirection: 'column',
        gap: showModeSelector ? 1 : 0,
        m: 0,
      }}
    >
      {showModeSelector && entityRef && (
        <PerformShowModeRow flow={flow} entityRef={entityRef} liveContextState={liveContextState} sx={{ mb: 0 }} />
      )}
      <Box
        sx={{
          display: 'flex',
          flex: forceTwoColumnLayout ? '0 0 auto' : 1,
          minHeight: forceTwoColumnLayout ? 'auto' : 0,
          gap: forceTwoColumnLayout ? 1 : 2,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          m: 0,
        }}
      >
        {/* Left column: Stages + Remote Speakers */}
        {showLeftColumn && (
          <Box
            sx={{
              ...colFlex,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: leftColumnStackGap,
              my: 0,
            }}
          >
            {/* Stages */}
            {showStagesPaper && (
              <Paper
                elevation={0}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    width: '100%',
                    minWidth: 0,
                    alignSelf: 'stretch',
                    pt: sectionContentPy,
                    pb: sectionContentPy,
                  }}
                >
                  {!hasStages ? (
                    !inlineHideEmptyStagesRemote ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                        {t('flows.detail.perform.noStages')}
                      </Typography>
                    ) : null
                  ) : (
                    stagePlayers.map((player) => {
                      const itemId = `roomCam:${player.id}-cam1`;
                      const legacySelected = selectedItemIds.includes(itemId);
                      const legacyPlayed = playedItemIds.has(itemId);
                      const { isSelected, isPlayed } = resolveCueRowState(itemId, legacySelected, legacyPlayed);
                      const cuePhase = usesAutoCue ? resolveCueItemState(cueItems, itemId)?.phase : undefined;
                      const isOnAir = isPerformItemOnAir(usesAutoCue, cuePhase, isSelected, isPlayed);
                      const canSelect = !!entityRef && (!usesAutoCue || !cueCommand.isPending);
                      const canPreview = canPreviewLiveKit(player);

                      return (
                        <Box
                          key={player.id}
                          onClick={() => canSelect && handleMainContentToggle(itemId)}
                          sx={
                            {
                              ...itemRowSx,
                              ...getPerformItemRowStyles(isSelected, isPlayed, isOnAir),
                              cursor: canSelect ? 'pointer' : 'default',
                              ...getPerformSelectableRowHoverSx(canSelect, isSelected, isPlayed, isOnAir),
                            } as SxProps<Theme>
                          }
                        >
                          <Typography variant="body2" sx={itemTitleSx}>
                            {player.name ?? player.id}
                          </Typography>
                          {entityRef && (isSelected || isPlayed) && renderAudibleControl(itemId)}
                          {canPreview && (
                            <Tooltip title={t('media.preview')}>
                              <IconButton size="small" onClick={(e) => handleStagePreview(e, player)} sx={{ p: 0.25 }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            )}

            {showStagesRemoteDivider && <Divider sx={{ width: '100%', my: forceTwoColumnLayout ? 0 : undefined }} />}

            {/* Remote Speakers */}
            {showRemoteSpeakersPaper && (
              <Paper
                elevation={0}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    width: '100%',
                    minWidth: 0,
                    alignSelf: 'stretch',
                    pt: sectionContentPy,
                    pb: sectionContentPy,
                  }}
                >
                  {!hasRemoteSpeakers ? (
                    !inlineHideEmptyStagesRemote ? (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                        {t('flows.detail.perform.noRemoteSpeakers')}
                      </Typography>
                    ) : null
                  ) : (
                    remoteSpeakerPlayers.map((player) => {
                      const itemId = remoteSpeakerItemId(player);
                      const isOnStage = speakersOnStage;
                      const canPreview = canPreviewLiveKit(player);

                      return (
                        <Box
                          key={player.id}
                          sx={
                            {
                              ...itemRowSx,
                              ...getPerformItemRowStyles(isOnStage, false, isOnStage),
                              cursor: 'default',
                            } as SxProps<Theme>
                          }
                        >
                          <Typography variant="body2" sx={itemTitleSx}>
                            {player.name ?? player.id}
                          </Typography>
                          {entityRef && isOnStage && renderAudibleControl(itemId)}
                          {canPreview && (
                            <Tooltip title={t('media.preview')}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleRemoteSpeakerPreview(e, player)}
                                sx={{ p: 0.25 }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* Right column: Media */}
        <Box
          sx={{
            ...colFlex,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            my: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              boxShadow: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              flex: '0 0 auto',
              alignSelf: 'stretch',
              my: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
                minWidth: 0,
                alignSelf: 'stretch',
                pt: sectionContentPy,
                pb: sectionContentPy,
              }}
            >
              {allMediaItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                  {t('flows.detail.perform.noMediaItems')}
                </Typography>
              ) : (
                allMediaItems.map((item) => {
                  const legacySelected = selectedItemIds.includes(item.itemId);
                  const legacyPlayed = playedItemIds.has(item.itemId);
                  const { isSelected, isPlayed } = resolveCueRowState(item.itemId, legacySelected, legacyPlayed);
                  const cuePhase = usesAutoCue ? resolveCueItemState(cueItems, item.itemId)?.phase : undefined;
                  const isOnAir = isPerformItemOnAir(usesAutoCue, cuePhase, isSelected, isPlayed);
                  const handleClick = () => entityRef && handleMainContentToggle(item.itemId, item.title);
                  const canSelectMedia = !!entityRef && (!usesAutoCue || !cueCommand.isPending);
                  const isVirtual = item.type === 'virtual';
                  const isScreenshare = item.type === 'screenshare';
                  const fileType = 'fileType' in item ? item.fileType : undefined;
                  const mediaAggregate =
                    item.type === 'media' && item.media ? resolveMediaItemAggregateStatus(item.media) : undefined;
                  const showPreviewButton =
                    isVirtual ||
                    isScreenshare ||
                    (item.type === 'media' && item.media && hasPreviewableMedia(item.media.assets ?? []));
                  const showInstructionsButton =
                    item.type === 'media' &&
                    item.media &&
                    !showPreviewButton &&
                    !!mediaAggregate?.insight &&
                    hasShareableProcessingInstructions(mediaAggregate.insight) &&
                    mediaAggregate.status !== 'approved';

                  return (
                    <Box
                      key={item.itemId}
                      onClick={handleClick}
                      sx={
                        {
                          ...itemRowSx,
                          ...getPerformItemRowStyles(isSelected, isPlayed, isOnAir),
                          cursor: canSelectMedia ? 'pointer' : 'default',
                          ...getPerformSelectableRowHoverSx(canSelectMedia, isSelected, isPlayed, isOnAir),
                          ...((isVirtual || isScreenshare) &&
                            !isOnAir && {
                              border: '2px dashed',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                            }),
                        } as SxProps<Theme>
                      }
                    >
                      {isScreenshare ? (
                        <ScreenShareOutlined sx={{ fontSize: 20, flexShrink: 0, color: 'text.secondary' }} />
                      ) : isVirtual ? null : mediaAggregate?.showSpinner ? (
                        <MediaItemProcessingSpinner size={20} status={mediaAggregate.insight?.status} />
                      ) : fileType ? (
                        <FileTypeIcon
                          fileType={fileType}
                          color={mapMediaItemStatusToneToIconColor(mediaAggregate?.tone ?? 'active')}
                          sx={{ fontSize: 20, flexShrink: 0 }}
                        />
                      ) : (
                        <InsertDriveFileOutlined sx={{ fontSize: 20, flexShrink: 0, color: 'text.disabled' }} />
                      )}
                      <Typography variant="body2" sx={itemTitleSx}>
                        {item.title}
                      </Typography>
                      {!usesAutoCue &&
                        item.type === 'media' &&
                        item.itemId.startsWith('media:') &&
                        fileType &&
                        hasSlideSupport(fileType) &&
                        (isSelected || isPlayed) && (
                          <SlideControlButton
                            mediaId={item.itemId.replace('media:', '')}
                            entityType="Flow"
                            entityId={bindingFlowId ?? ''}
                            disabled={!entityRef}
                            currentSlideIndex={connectedPlayer?.currentSlideIndex ?? sequenceIndexStatus?.index}
                            totalSlides={connectedPlayer?.totalSlides ?? sequenceIndexStatus?.frameCount}
                            speakerNotes={connectedPlayer?.speakerNotes}
                            mediaType={resolveSlideControlMediaType(fileType)}
                            frameSlideNumbers={resolveMediaFrameSlideNumbers(item.media)}
                          />
                        )}
                      {showPreviewButton ? (
                        <Tooltip title={t('media.preview')}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMediaPreview(e, item)}
                            disabled={controlPreviewMutation.isPending}
                            sx={{ p: 0.25 }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {showInstructionsButton ? (
                        <Tooltip title={t('media.processing.instructionsModal.openHint')}>
                          <IconButton size="small" onClick={(e) => handleMediaPreview(e, item)} sx={{ p: 0.25 }}>
                            <InfoOutlined fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {liveKitPreview && (
        <LiveKitPreviewModal
          open={!!liveKitPreview}
          onClose={() => setLiveKitPreview(null)}
          livekitRoomId={liveKitPreview.livekitRoomId}
          title={liveKitPreview.title}
          publisherPlayerId={liveKitPreview.publisherPlayerId}
          showCamera={liveKitPreview.showCamera ?? true}
          showScreenshare={liveKitPreview.showScreenshare ?? true}
        />
      )}

      {mediaPreview && (
        <PreviewModal
          open={!!mediaPreview}
          loading={mediaPreview.loading}
          onClose={() => setMediaPreview(null)}
          playType={mediaPreview.playType}
          playerSyncData={mediaPreview.loading ? undefined : mediaPreview.playerSyncData}
          title={mediaPreview.title}
          processingInstructions={mediaPreview.processingInstructions}
          onPlay={() =>
            setPlayConfirm({
              itemId: mediaPreview.itemId,
              title: mediaPreview.title,
              startSlideIndex: resolveStartSlideIndexForTake(mediaPreview.itemId),
            })
          }
          playDisabled={
            !entityRef || cueCommand.isPending || resolveCueItemState(cueItems, mediaPreview.itemId)?.phase === 'on_air'
          }
          slideNavigationScope="local"
        />
      )}

      {instructionsPreview ? (
        <MediaProcessingInstructionsModal
          open={!!instructionsPreview}
          onClose={() => setInstructionsPreview(null)}
          insight={instructionsPreview.insight}
          context={instructionsPreview.context}
        />
      ) : null}

      <PlayCueConfirmationModal
        open={!!playConfirm}
        mediaTitle={playConfirm?.title ?? ''}
        showCuePlayModes={usesAutoCue}
        onJumpCue={() => {
          if (playConfirm) {
            executeCuePlayMedia(playConfirm.itemId, 'jump', playConfirm.startSlideIndex);
          }
          setPlayConfirm(null);
          setMediaPreview(null);
        }}
        onPlayInsert={() => {
          if (playConfirm) {
            executeCuePlayMedia(playConfirm.itemId, 'insert', playConfirm.startSlideIndex);
          }
          setPlayConfirm(null);
          setMediaPreview(null);
        }}
        onConfirm={() => {
          if (playConfirm) {
            executeCuePlayMedia(playConfirm.itemId, 'insert', playConfirm.startSlideIndex);
          }
          setPlayConfirm(null);
          setMediaPreview(null);
        }}
        onCancel={() => setPlayConfirm(null)}
        isLoading={cueCommand.isPending}
      />

      <Menu
        id={PERFORM_AUDIBLE_MENU_ID}
        anchorEl={audibleMenu?.anchor ?? null}
        open={!!audibleMenu}
        onClose={closeAudibleMenu}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          list: {
            'aria-labelledby': audibleMenu ? `perform-audible-override-button-${audibleMenu.itemId}` : undefined,
          },
        }}
      >
        {audibleMenu ? (
          <>
            <MenuItem
              onClick={() =>
                applyAudibleOverride(audibleMenu.itemId, {
                  mode: 'segment',
                  audible: false,
                })
              }
            >
              <ListItemIcon>
                <VolumeOff fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('flows.detail.perform.muteUntilNextSegment', undefined, 'Mute until next segment')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                applyAudibleOverride(audibleMenu.itemId, {
                  mode: 'segment',
                  audible: true,
                })
              }
            >
              <ListItemIcon>
                <VolumeUp fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('flows.detail.perform.unmuteUntilNextSegment', undefined, 'Unmute until next segment')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                applyAudibleOverride(audibleMenu.itemId, {
                  mode: 'permanent',
                  audible: false,
                })
              }
            >
              <ListItemIcon>
                <VolumeMute fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('flows.detail.perform.alwaysMute', undefined, 'Always mute')}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() =>
                applyAudibleOverride(audibleMenu.itemId, {
                  mode: 'permanent',
                  audible: true,
                })
              }
            >
              <ListItemIcon>
                <VolumeUp fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t('flows.detail.perform.alwaysUnmute', undefined, 'Always unmute')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => applyAudibleOverride(audibleMenu.itemId, { clearOverride: true })}>
              <ListItemIcon>
                <RestartAlt fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('flows.detail.perform.followSegmentAudio', undefined, 'Follow segment default')}
              </ListItemText>
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </Box>
  );
}
